import { useMemo, useState } from 'react';
import { api } from '../../lib/api.js';
import { COLLECTOR_CATEGORIES, ALL_COLLECTOR_IDS, SOURCE_LICENSES, licenseRequirement } from '../../../../shared/collectors.mjs';
import { ALL_PERMISSIONS, permissionsForSelection } from '../../../../shared/permission-map.mjs';
import {
  CHECK_REQUIRED_SOURCES, ESSENTIAL_PERMISSIONS, ESSENTIAL_SOURCES, presetOf, selectionForPreset,
} from '../../../../shared/permission-presets.mjs';
import PermissionConsentTable, { AddOnPermissions } from '../../components/PermissionConsentTable.jsx';
import { categoryTitle } from '../data-collection/SourcesCard.jsx';

// ENTRAID-350 "start small": the first part of Step 1. The admin chooses what onGuard reads BEFORE the Microsoft consent,
// sees each permission with its reason, and only then gets the consent link. Product owner decision (2026-10-22): exactly
// two choices, "Default" (the recommended starter set, internally the Essentials preset, preselected) and "Customize"
// (every source, starting from the Default set checked, with Select all). There is no separate "All data sources" option.
// The choice is recorded on the customer (POST /permissions), so the customer's own Entra app is created
// with exactly those permissions. Adding sources later re-consents only the new permissions (Data collection page).

const CHECK_COUNT = Object.keys(CHECK_REQUIRED_SOURCES).length;

/** How many security checks a permission list can run (every source of the check is readable). */
export function runnableChecks(permissions) {
  const have = new Set(permissions);
  const sourcePerms = (s) => permissionsForSelection({ mode: 'custom', sources: [s] });
  return Object.values(CHECK_REQUIRED_SOURCES).filter((sources) => sources.every((s) => sourcePerms(s).every((p) => have.has(p)))).length;
}

/** What the Default set leaves out, said in plain words and only for what it really leaves out (computed from the data). */
export function leftOutPhrases(sources = ESSENTIAL_SOURCES, permissions = ESSENTIAL_PERMISSIONS) {
  const has = new Set(sources);
  const phrases = [];
  if (ALL_COLLECTOR_IDS.some((id) => !has.has(id) && SOURCE_LICENSES[id] === 'P2')) phrases.push('what needs Microsoft Entra ID P2');
  if (!permissions.includes('Sites.Read.All')) phrases.push('SharePoint sites (least privilege)');
  if (!permissions.includes('Reports.Read.All')) phrases.push('the Microsoft 365 usage reports');
  return phrases;
}

const joinPhrases = (list) => (list.length < 2 ? list.join('') : `${list.slice(0, -1).join(', ')} and ${list[list.length - 1]}`);

export const OPTIONS = Object.freeze([
  {
    id: 'essentials',
    title: 'Default',
    badge: 'Recommended',
    text: `${ESSENTIAL_PERMISSIONS.length} of ${ALL_PERMISSIONS.length} read-only permissions and ${ESSENTIAL_SOURCES.length} of ${ALL_COLLECTOR_IDS.length} data sources. Powers the onGuard score, ${runnableChecks(ESSENTIAL_PERMISSIONS)} of ${CHECK_COUNT} security checks and Watch. Leaves out ${joinPhrases(leftOutPhrases())}.`,
  },
  { id: 'custom', title: 'Customize', text: 'Choose the data sources yourself, starting from the Default set. The permissions follow your choice.' },
]);

/** The choice a customer record starts on: Default unless it recorded something else (then Customize with that). */
export function initialChoice(customer) {
  const s = customer?.permissionSelection;
  if (!s) return { preset: 'essentials', custom: [...ESSENTIAL_SOURCES] };
  const preset = presetOf(s);
  if (preset === 'essentials') return { preset, custom: [...ESSENTIAL_SOURCES] };
  return { preset: 'custom', custom: preset === 'all' ? [...ALL_COLLECTOR_IDS] : [...(s.sources ?? ESSENTIAL_SOURCES)] };
}

/**
 * The notice for a 409 "consent in progress" (security review of PR 395, L3): a consent link issued earlier holds the
 * selection until it is used or expires, so the admin is told what that link asks for and until when, never moved on
 * silently. `body` is the API's 409 answer ({ requestedPermissions, expiresAt }).
 */
export function earlierConsentNotice(body) {
  const list = Array.isArray(body?.requestedPermissions) ? body.requestedPermissions.filter((p) => typeof p === 'string') : [];
  const at = Date.parse(body?.expiresAt ?? '');
  const what = list.length > 0 ? `${list.length} read-only permission${list.length === 1 ? '' : 's'} (${list.join(', ')})` : 'the permissions chosen then';
  const until = Number.isFinite(at)
    ? `it expires at ${new Date(at).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' })}`
    : 'it expires (24 hours after it was issued)';
  return `A consent link issued earlier asks for ${what}. Use it, or wait until ${until}.`;
}

export default function DataSourceChoice({ customer, onChosen }) {
  const start = useMemo(() => initialChoice(customer), [customer]);
  const [preset, setPreset] = useState(start.preset);
  const [custom, setCustom] = useState(() => new Set(start.custom));
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  // The 409 notice: an earlier consent link holds the selection (L3).
  const [earlier, setEarlier] = useState(null);

  // Every source ticked in Customize is recorded as "all" (one consent for every permission), like Data collection does.
  const selection = preset === 'custom' && custom.size >= ALL_COLLECTOR_IDS.length
    ? selectionForPreset('all')
    : selectionForPreset(preset, [...custom]);
  const allChecked = custom.size >= ALL_COLLECTOR_IDS.length;
  const permissions = permissionsForSelection(selection);
  const empty = preset === 'custom' && custom.size === 0;

  function toggle(id) {
    setCustom((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }

  async function submit() {
    if (empty) return;
    setBusy(true); setErr(null); setEarlier(null);
    try {
      await api.updatePermissions(customer.customerId, selection);
      onChosen(selection);
    } catch (e) {
      // A consent link issued earlier holds the selection until it is used or expires (24 hours): say so, and let the admin
      // choose to use it.
      if (e?.status === 409 && e?.body?.reason === 'consent-in-progress') { setEarlier(earlierConsentNotice(e.body)); return; }
      setErr(e.message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="space-y-5" data-testid="data-source-choice">
      <fieldset className="space-y-2">
        <legend className="text-sm font-semibold text-ipc-ink">What should onGuard read?</legend>
        {OPTIONS.map((o) => (
          <label
            key={o.id}
            className={`flex cursor-pointer gap-3 rounded-xl border px-4 py-3 ${preset === o.id ? 'border-ipc-light bg-ipc-surface' : 'border-ipc-border bg-white'}`}
          >
            <input type="radio" name="data-source-preset" value={o.id} checked={preset === o.id} onChange={() => setPreset(o.id)} className="mt-1" />
            <span className="min-w-0">
              <span className="text-sm font-semibold text-ipc-ink">{o.title}</span>
              {o.badge ? <span className="ml-2 rounded border border-ipc-border px-1.5 text-[11.5px] font-semibold text-ipc-primary">{o.badge}</span> : null}
              <span className="mt-0.5 block text-[13px] text-ipc-muted">{o.text}</span>
            </span>
          </label>
        ))}
      </fieldset>

      {preset === 'custom' ? (
        <div className="space-y-2">
        <div className="flex flex-wrap items-center gap-3 text-[13px]">
          <span className="text-ipc-muted">{custom.size} of {ALL_COLLECTOR_IDS.length} data sources selected.</span>
          <button type="button" className="tap font-semibold text-ipc-light hover:underline" onClick={() => setCustom(new Set(allChecked ? [] : ALL_COLLECTOR_IDS))}>
            {allChecked ? 'Clear all' : 'Select all'}
          </button>
          <button type="button" className="tap font-semibold text-ipc-light hover:underline" onClick={() => setCustom(new Set(ESSENTIAL_SOURCES))}>
            Back to the Default set
          </button>
        </div>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2" data-testid="custom-sources">
          {COLLECTOR_CATEGORIES.map((cat) => (
            <fieldset key={cat.id} className="rounded-xl border border-ipc-border px-3 py-2">
              <legend className="px-1 text-[13px] font-semibold text-ipc-ink">{categoryTitle(cat)}</legend>
              {cat.collectors.map((c) => (
                <label key={c.id} className="flex items-start gap-2 py-0.5 text-[13px] text-ipc-ink">
                  <input type="checkbox" checked={custom.has(c.id)} onChange={() => toggle(c.id)} className="mt-0.5" />
                  <span>
                    {c.label}
                    {licenseRequirement(c.id) ? <span className="text-ipc-muted"> ({licenseRequirement(c.id).replace(/^Requires /, 'needs ')})</span> : null}
                  </span>
                </label>
              ))}
            </fieldset>
          ))}
        </div>
        </div>
      ) : null}

      <PermissionConsentTable permissions={permissions} />
      <AddOnPermissions permissions={permissions} />

      {err ? <p className="text-sm text-ipc-bad" role="alert">{err}</p> : null}
      {earlier ? (
        <div className="space-y-2 rounded-xl border border-ipc-border bg-ipc-surface px-4 py-3" role="status" data-testid="earlier-consent">
          <p className="text-sm text-ipc-ink">{earlier}</p>
          <button type="button" className="tap text-sm font-semibold text-ipc-light hover:underline" onClick={() => onChosen(null)}>
            Use the earlier consent link
          </button>
        </div>
      ) : null}
      <div className="flex flex-wrap items-center gap-3">
        <button type="button" className="btn-primary" onClick={submit} disabled={busy || empty}>
          {busy ? 'Saving...' : 'Continue to Microsoft consent'}
        </button>
        {empty ? <span className="text-[13px] text-ipc-muted">Choose at least one data source.</span> : (
          <span className="text-[13px] text-ipc-muted">You can add or remove data sources later from Data collection.</span>
        )}
      </div>
    </div>
  );
}
